import React, { useState } from "react"
import Slider from "react-slick"
import styled, { css } from "styled-components"
import { Link } from "gatsby"
import Img from "gatsby-image"

import { singularize } from "../../utils/common/transforms"

import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"

import media, { sizes } from "../../styles/media"
import { FadeInTransition } from "../common"
import { CarouselArrowButton } from "../artist/CarouselArrow"

const HeroWrapper = styled.div`
  height: 85vh;
  max-width: 100vw;
  position: relative;

  ${media.tablet`
    height: 100vh;
  `}
`

const HeroImage = styled(Img)`
  align-items: center;
  background-size: cover;
  justify-content: center;
  position: relative;
  width: 100%;
  height: 85vh;
  object-fit: cover;
  object-position: center center;
  z-index: 1;

  ${media.tablet`
    height: 100vh;
  `}
`

const SliderWrapper = styled.div`
  grid-column-end: span 12;
  min-width: 0;
  transition: opacity 800ms ease-in-out;

  .slick-list,
  .slider,
  .slick-track {
    height: 85vh;

    ${media.tablet`
      height: 100vh;
    `}
  }

  ${media.tablet`
    grid-column-end: span 12;
  `}

  ${media.desktop`
    grid-column-end: span 12;  
  `}
`

const HeroTitle = styled.div`
  font-family: StanleyBold, serif;
  font-weight: 700;
  font-style: normal;
  color: ${props => (props.dark ? props.theme.black : props.theme.white)};
  left: 20px;
  bottom: 26px;
  position: absolute;
  z-index: 2;
  max-width: 65%;

  ${media.tablet`
    left: 40px;
    bottom: 46px;
  `}

  a {
    color: inherit;
    text-decoration: none;

    &:hover {
      color: ${props => props.theme.red};
    }
  }

  h1,
  h2 {
    display: inline;
    font-size: 20px;
    letter-spacing: -0.27px;
    color: inherit;

    ${media.tablet`
      font-size: 45px;
      letter-spacing: -0.61px;
    `}
  }

  h1 {
    font-family: StanleyPoster, serif;
    font-weight: lighter;
    letter-spacing: -0.57px;
  }

  h2 {
    font-family: StanleyRegular, serif;
    font-style: italic;
    font-weight: normal;
  }
`

const HeroCarouselArrowButton = styled(CarouselArrowButton)`
  && {
    z-index: 2;

    ${props => {
      return props.direction === "left"
        ? css`
            left: 20px;

            ${media.tablet`
              left: 40px;
            `}
          `
        : css`
            right: 20px;

            ${media.tablet`
              right: 40px;
            `}
          `
    }}
  }
`

const HeroCarouselArrow = ({
  direction = "left",
  useDarkLayout = false,
  onClick,
}) => (
  <HeroCarouselArrowButton onClick={onClick} direction={direction}>
    <svg
      width={18}
      height={19}
      // The exported SVG asset is a left facing arrow, so if we're rendering
      // a right arrow, we need to flip it
      style={{
        transform: direction === "right" ? "scaleX(-1)" : "",
      }}
    >
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g
          transform="translate(-601.000000, -1845.000000)"
          stroke={useDarkLayout ? "#000000" : "#FFFFFF"}
          strokeWidth="1.33"
          className="arrow-line"
        >
          <g transform="translate(34.000000, 1402.000000)">
            <g transform="translate(575.500000, 452.500000) rotate(-270.000000) translate(-575.500000, -452.500000) translate(567.000000, 444.000000)">
              <polyline points="17 7.59807692 8.49933847 15.5980769 -5.09038573e-17 7.59807692" />
              <path d="M8.4995,15.7231538 L8.5005,-0.276846154" />
            </g>
          </g>
        </g>
      </g>
    </svg>
  </HeroCarouselArrowButton>
)

export function HeroCarousel({ heroArtists, onSlideChange }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [slider, setSlider] = useState(null)

  const sliderRef = element => {
    setSlider(element)
  }

  function getCategorySubtitleLink(category) {
    const isChildCategory =
      category && category.parentCategory && category.parentCategory.length

    let subtitleLink = ""

    if (category) {
      subtitleLink = isChildCategory
        ? `/roster?category=${encodeURIComponent(
            category.parentCategory[0].name
          )}&subcategories=${encodeURIComponent(category.name)}`
        : `/roster?category=${encodeURIComponent(category.name)}`
    }

    return subtitleLink
  }

  return (
    <FadeInTransition duration={800}>
      {({ defaultStyle, transitionStyles }) => (
        <>
          <SliderWrapper style={{ ...defaultStyle, ...transitionStyles }}>
            <Slider
              dots={false}
              infinite={true}
              slidesToShow={1}
              slidesToScroll={1}
              draggable={false}
              swipe={false}
              autoplay={true}
              arrows={false}
              autoplaySpeed={3000}
              pauseOnFocus={false}
              pauseOnHover={false}
              beforeChange={(_, newIndex) => {
                setCurrentIndex(newIndex)
                onSlideChange(heroArtists[newIndex])
              }}
              ref={sliderRef}
            >
              {heroArtists.map((artist, i) => {
                let heroImage = artist.heroImage
                let mobileHeroImage = artist.mobileHeroImage
                  ? artist.mobileHeroImage
                  : artist.croppedMobileHeroImage

                if (artist.heroCarouselImage) {
                  heroImage = artist.heroCarouselImage
                  mobileHeroImage = artist.mobileHeroImage
                    ? artist.mobileHeroImage
                    : artist.croppedMobileHeroCarouselImage
                }

                const heroSources = heroImage
                  ? [
                      mobileHeroImage.fluid,
                      {
                        ...heroImage.fluid,
                        media: `(min-width: ${sizes.tablet}px)`,
                      },
                    ]
                  : {}

                return (
                  <HeroWrapper>
                    <HeroImage fluid={heroSources} fadeIn={false} />
                    <HeroTitle dark={artist.useDarkLayout}>
                      <div>
                        <Link to={`/artists/${artist.slug}`}>
                          <h1>{artist.name} </h1>
                        </Link>
                        {artist.categories &&
                          artist.categories.map((category, i) => {
                            return (
                              <Link to={getCategorySubtitleLink(category)}>
                                <h2>
                                  {category.keepPlural
                                    ? category.name
                                    : singularize(category.name) +
                                      (i === artist.categories.length - 1
                                        ? ""
                                        : ", ")}{" "}
                                </h2>
                              </Link>
                            )
                          })}
                      </div>
                    </HeroTitle>
                  </HeroWrapper>
                )
              })}
            </Slider>
          </SliderWrapper>
          {slider && (
            <>
              <HeroCarouselArrow
                direction="left"
                useDarkLayout={heroArtists[currentIndex].useDarkLayout}
                onClick={slider.slickPrev}
              />

              <HeroCarouselArrow
                direction="right"
                useDarkLayout={heroArtists[currentIndex].useDarkLayout}
                onClick={slider.slickNext}
              />
            </>
          )}
        </>
      )}
    </FadeInTransition>
  )
}
